import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getImageUrl } from '../utils/cdnUtils'
import { useWallet } from '../contexts/WalletContext'
import { CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES, formatCustomerSlipCancelWindowText, lotteryService } from '../services/lotteryService'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'
import type { LotteryRound } from '../services/lotteryService'
import RulesGuideModal from '../components/RulesGuideModal'

type BettingPackage = {
  code: string
  name: string
  packageType: 'RATE_BASED' | 'SET_BASED'
  setConfig?: {
    fixedPrice: number
    fixedPayout: number
    discount: number
    includedPrizeTypes?: Array<{
      prizeTypeCode: string
      prizeTypeName?: string
      digitCount?: number
    }>
  }
  prizeConfigs?: Array<{
    prizeTypeCode: string
    prizeTypeName?: string
    payoutRate?: number
    isActive?: boolean
  }>
}

type SetLottoState = {
  round?: LotteryRound
  package?: BettingPackage
  packageCode?: string
  fixedPrice?: number
  backTo?: string
  backLabel?: string
}

const SET_PAYOUT_ROWS = [
  { label: '4 ตัวตรง', large: '100,000', medium: '50,000', small: '25,000' },
  { label: '3 ตัวตรง', large: '35,000', medium: '17,500', small: '8,000' },
  { label: '4 ตัวโต๊ด', large: '5,000', medium: '2,500', small: '1,250' },
  { label: '3 ตัวโต๊ด', large: '3,500', medium: '1,750', small: '800' },
  { label: '2 ตัวหน้า', large: '1,600', medium: '700', small: '350' },
  { label: '2 ตัวหลัง', large: '1,600', medium: '700', small: '350' },
]
const SET_DIGIT_COUNT = 4
const SET_FOUR_DIGIT_PRIZE_CODE_PREFERENCE = ['4_DIGIT_EXACT', '4_DIGIT_TOP', 'FIRST_PRIZE'] as const
const SET_PACKAGE_COLUMNS = [
  { key: 'large', codeHints: ['LARGE', 'ใหญ่'], label: 'ชุดใหญ่' },
  { key: 'medium', codeHints: ['MEDIUM', 'กลาง'], label: 'ชุดกลาง' },
  { key: 'small', codeHints: ['SMALL', 'เล็ก'], label: 'ชุดเล็ก' },
] as const

const SET_PRIZE_LABEL_BY_CODE: Record<string, string> = {
  '4_DIGIT_EXACT': '4 ตัวตรง',
  '4_DIGIT_TOP': '4 ตัวตรง',
  FIRST_PRIZE: '4 ตัวตรง',
  '3_DIGIT_TOP': '3 ตัวตรง',
  '4_DIGIT_TOD': '4 ตัวโต๊ด',
  '3_DIGIT_TOD': '3 ตัวโต๊ด',
  '2_DIGIT_FRONT': '2 ตัวหน้า',
  '2_DIGIT_TOP': '2 ตัวหน้า',
  '2_DIGIT_BACK': '2 ตัวหลัง',
  '2_DIGIT_BOTTOM': '2 ตัวหลัง',
}

const SET_PRIZE_DISPLAY_ORDER = [
  '4 ตัวตรง',
  '3 ตัวตรง',
  '4 ตัวโต๊ด',
  '3 ตัวโต๊ด',
  '2 ตัวหน้า',
  '2 ตัวหลัง',
]

function resolveSetFourDigitPrizeTypeCode(selectedPackage?: BettingPackage): string {
  const supportedCodes = new Set<string>()

  for (const config of selectedPackage?.prizeConfigs ?? []) {
    const code = String(config.prizeTypeCode || '').trim().toUpperCase()
    if (code) supportedCodes.add(code)
  }

  for (const config of selectedPackage?.setConfig?.includedPrizeTypes ?? []) {
    const code = String(config.prizeTypeCode || '').trim().toUpperCase()
    if (code) supportedCodes.add(code)
  }

  return SET_FOUR_DIGIT_PRIZE_CODE_PREFERENCE.find((code) => supportedCodes.has(code)) ?? '4_DIGIT_TOP'
}

function getPackageLabel(pkg?: BettingPackage, code?: string): string {
  if (pkg?.name) return pkg.name
  switch (code) {
    case 'SET_LARGE':
      return 'ชุดใหญ่'
    case 'SET_MEDIUM':
      return 'ชุดกลาง'
    default:
      return 'ชุดเล็ก'
  }
}

function formatSetPayoutAmount(value?: number | null): string {
  const amount = Number(value || 0)
  return amount > 0 ? amount.toLocaleString('th-TH') : '-'
}

function getSetPackageColumnKey(pkg?: BettingPackage, code?: string): typeof SET_PACKAGE_COLUMNS[number]['key'] | null {
  const combined = `${pkg?.code || ''} ${code || ''} ${pkg?.name || ''}`.toUpperCase()
  const matched = SET_PACKAGE_COLUMNS.find((column) =>
    column.codeHints.some((hint) => combined.includes(hint.toUpperCase()))
  )
  return matched?.key || null
}

function buildSetPayoutRows(selectedPackage?: BettingPackage, packageCode?: string) {
  const packageColumn = getSetPackageColumnKey(selectedPackage, packageCode)
  if (!selectedPackage?.prizeConfigs?.length || !packageColumn) {
    return SET_PAYOUT_ROWS
  }

  const valuesByLabel = new Map<string, string>()
  for (const config of selectedPackage.prizeConfigs) {
    if (config.isActive === false) continue
    const code = String(config.prizeTypeCode || '').trim().toUpperCase()
    const label = SET_PRIZE_LABEL_BY_CODE[code] || config.prizeTypeName || code
    if (!label || valuesByLabel.has(label)) continue
    valuesByLabel.set(label, formatSetPayoutAmount(config.payoutRate))
  }

  return SET_PRIZE_DISPLAY_ORDER.map((label) => {
    const fallback = SET_PAYOUT_ROWS.find((row) => row.label === label)
    return {
      label,
      large: packageColumn === 'large' ? valuesByLabel.get(label) || fallback?.large || '-' : fallback?.large || '-',
      medium: packageColumn === 'medium' ? valuesByLabel.get(label) || fallback?.medium || '-' : fallback?.medium || '-',
      small: packageColumn === 'small' ? valuesByLabel.get(label) || fallback?.small || '-' : fallback?.small || '-',
    }
  })
}

function getRoundFlag(round?: LotteryRound): string {
  const name = `${round?.huayName ?? ''} ${round?.huayTypeName ?? ''}`.toLowerCase()
  if (name.includes('ลาว')) return 'flag/la.png'
  if (name.includes('เวียดนาม') || name.includes('ฮานอย')) return 'flag/vn.png'
  if (name.includes('จีน')) return 'flag/cn.png'
  if (name.includes('ญี่ปุ่น') || name.includes('นิเคอิ')) return 'flag/jp.png'
  if (name.includes('ฮั่งเส็ง')) return 'flag/hk.png'
  if (name.includes('สิงคโปร์')) return 'flag/sg.png'
  if (name.includes('มาเลย์')) return 'flag/ml.png'
  return 'flag/th.png'
}

function translateSetBetError(message?: string, errors?: Array<{ reason?: string; number?: string }>): string {
  const rawMessage = String(message || '').trim()
  const firstError = Array.isArray(errors) ? errors.find((item) => item?.reason || item?.number) : null
  const reason = String(firstError?.reason || '').trim()
  const lowerReason = reason.toLowerCase()
  const lowerMessage = rawMessage.toLowerCase()
  const number = String(firstError?.number || '').trim()

  if (/not available in package|ไม่รองรับในแพ็กเกจ|betting package not found|package not found|ไม่พบแพ็กเกจการเดิมพัน/i.test(rawMessage)) {
    return 'แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่'
  }

  if (
    lowerReason.includes('number is blocked or full') ||
    lowerReason.includes('number has reached its betting limit') ||
    lowerReason.includes('would exceed total betting limit') ||
    lowerMessage.includes('some bet items are no longer valid')
  ) {
    return number
      ? `เลข ${number} ไม่สามารถซื้อได้แล้ว กรุณาลบออกจากรายการแล้วลองใหม่`
      : 'มีบางรายการไม่สามารถซื้อได้แล้ว กรุณาลบเลขที่มีปัญหาออกแล้วลองใหม่'
  }

  if (lowerReason.includes('not active')) return 'รายการนี้ปิดรับแทงแล้ว กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('prize type not found')) return 'ไม่พบประเภทรางวัลของรายการนี้ กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('invalid number length')) return 'รูปแบบเลขไม่ถูกต้อง กรุณาลบรายการนี้แล้วเลือกใหม่'

  return rawMessage || 'ส่งโพยไม่สำเร็จ'
}

const SetLottoGamePage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { walletData, isLoadingWallet, formatCurrency, refreshWallet } = useWallet()
  const walletBalance = (walletData?.balance?.main ?? 0) + (walletData?.balance?.bonus ?? 0)
  const state = (location.state as SetLottoState | null) || {}
  const round = state.round
  const selectedPackage = state.package
  const packageCode = selectedPackage?.code || state.packageCode || ''
  const fixedPrice = selectedPackage?.setConfig?.fixedPrice ?? state.fixedPrice ?? 0
  const fixedDiscount = selectedPackage?.setConfig?.discount ?? 0
  const packageLabel = getPackageLabel(selectedPackage, packageCode)
  const setFourDigitPrizeTypeCode = resolveSetFourDigitPrizeTypeCode(selectedPackage)
  const setPayoutRows = buildSetPayoutRows(selectedPackage, packageCode)
  const backTo = state.backTo || '/set-terms'
  const backLabel = state.backLabel || 'หน้าแทงหวย'
  const displayTitle = round?.huayName || 'หวยชุด'
  const displayCountdown = round?.betEndTime ? lotteryService.formatTimeRemaining(round.betEndTime) : '00:00:00'
  const closeTimeLabel = round?.betEndTime
    ? new Date(round.betEndTime).toLocaleString('th-TH', {
        timeZone: 'Asia/Bangkok',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-'

  const [showModal, setShowModal] = useState(false)
  const [cancelWindowMinutes, setCancelWindowMinutes] = useState(CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES)
  const [numbers, setNumbers] = useState(Array.from({ length: SET_DIGIT_COUNT }, () => ''))
  const [amount, setAmount] = useState('')
  const [selectedSets, setSelectedSets] = useState(1)
  const [totalBet, setTotalBet] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let isMounted = true
    void lotteryService.getCustomerSlipCancelWindowMinutes().then((minutes) => {
      if (isMounted) setCancelWindowMinutes(minutes)
    })
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    setTotalBet(selectedSets * fixedPrice)
  }, [selectedSets, fixedPrice])

  useEffect(() => {
    if (!round && location.pathname === '/set-lotto-game') {
      toast.error('กรุณาเลือกรอบและแพ็กเกจจากหน้าแทงหวยชุดก่อน')
      navigate('/lotto', { replace: true })
    }
  }, [round, location.pathname, navigate])

  useEffect(() => {
    if (!round || location.pathname !== '/set-lotto-game') return
    if (!selectedPackage || selectedPackage.packageType !== 'SET_BASED' || !selectedPackage.setConfig || !selectedPackage.code) {
      toast.error('แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่')
      navigate(backTo, { replace: true, state: { round } })
    }
  }, [backTo, location.pathname, navigate, round, selectedPackage])

  const handleNumberInput = (index: number, value: string) => {
    const newNumbers = [...numbers]
    newNumbers[index] = value
    setNumbers(newNumbers)
  }
  
  const handleNumberButton = (num: string) => {
    const emptyIndex = numbers.findIndex(n => n === '')
    if (emptyIndex !== -1) {
      handleNumberInput(emptyIndex, num)
    }
  }
  
  const handleBackspace = () => {
    let lastFilledIndex = -1
    for (let i = numbers.length - 1; i >= 0; i -= 1) {
      if (numbers[i] !== '') {
        lastFilledIndex = i
        break
      }
    }
    if (lastFilledIndex !== -1) {
      handleNumberInput(lastFilledIndex, '')
    }
  }
  
  const handleRandom = () => {
    const randomNumbers = Array.from({ length: SET_DIGIT_COUNT }, () => 
      Math.floor(Math.random() * 10).toString()
    )
    setNumbers(randomNumbers)
  }
  
  const handleClear = () => {
    setNumbers(Array.from({ length: SET_DIGIT_COUNT }, () => ''))
    setAmount('')
    setSelectedSets(0)
    setTotalBet(0)
  }
  
  const handleSetSelection = (sets: number) => {
    setSelectedSets(sets)
    setAmount(sets.toString())
    setTotalBet(sets * fixedPrice)
  }

  const handleAmountChange = (value: string) => {
    setAmount(value)
    const sets = parseInt(value, 10) || 0
    setSelectedSets(sets)
    setTotalBet(sets * fixedPrice)
  }

  const handleSubmitBet = async () => {
    const numberStr = numbers.join('').trim()
    if (numberStr.length < SET_DIGIT_COUNT) {
      toast.error('กรุณาระบุตัวเลขอย่างน้อย 4 หลัก (หวยฮานอยชุดใช้ 4 หลัก)')
      return
    }
    if (totalBet <= 0) {
      toast.error('กรุณาเลือกจำนวนชุด')
      return
    }
    if (!round?._id) {
      toast.error('ไม่พบข้อมูลรอบหวย กรุณาเลือกจากหน้าแทงหวยชุดก่อน')
      navigate('/lotto', { replace: true })
      return
    }
    if (!packageCode || !selectedPackage?.setConfig) {
      toast.error('แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่')
      navigate(backTo, { replace: true, state: { round } })
      return
    }
    const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null
    const user = userStr ? JSON.parse(userStr) : null
    const userId = user?.id || user?.userId || user?.sub
    if (!userId) {
      toast.error('กรุณาเข้าสู่ระบบก่อนส่งโพย')
      return
    }
    if (totalBet > walletBalance) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดเงินไม่เพียงพอ',
        html: `ยอดเครดิตคงเหลือ <strong>${formatCurrency(walletBalance)}</strong> บาท น้อยกว่ายอดที่ต้องการแทง <strong>${formatCurrency(totalBet)}</strong> บาท<br><br>กรุณาเติมเงินหรือลดจำนวนเดิมพัน`,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#fd7e14'
      })
      return
    }

    setSubmitting(true)
    try {
      const fourDigits = numberStr.slice(0, SET_DIGIT_COUNT)
      const betItems = [
        { prizeTypeCode: setFourDigitPrizeTypeCode, number: fourDigits, amount: totalBet }
      ]
      const result = await lotteryService.placeBet(
        userId,
        round._id,
        packageCode,
        betItems,
        'web',
        user?.username
      )
      const slipId = result?.data?.slipId
      if (result?.success && slipId) {
        refreshWallet().catch(() => {})
        await Swal.fire({
          icon: 'success',
          title: 'ส่งโพยเรียบร้อย',
          text: 'โพยของคุณได้รับการบันทึกแล้ว',
          confirmButtonText: 'ดูโพย',
          showCancelButton: true,
          cancelButtonText: 'แทงต่อ'
        }).then((res) => {
          if (res.isConfirmed) {
            navigate(`/ticket-detail/${slipId}`)
          } else {
            handleClear()
          }
        })
      } else {
        toast.success('ส่งโพยเรียบร้อย')
        handleClear()
      }
    } catch (err: any) {
      const responseData = err?.response?.data
      const msg = responseData?.message || err?.message || 'ส่งโพยไม่สำเร็จ'
      const translatedMessage = translateSetBetError(msg, responseData?.errors)
      if (translatedMessage === 'แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่') {
        toast.error('แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่')
        navigate(backTo, { replace: true, state: { round } })
        return
      }
      toast.error(translatedMessage)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a 
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(backTo, { state: round ? { round } : undefined })
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      {backLabel}
                    </a>

                    <a
                      href="#"
                      className="bdr-5 c-white bgg-4 txt-link px-2"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowModal(true)
                      }}
                    >
                      กติกา & วิธีเล่น
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="row mx--1">
                      <div className="col-12 col-lg-6 px-1 mb-2">
                        <div className="">
                          <div className="bdr-5 bgc-4 c-dark px-2 py-1 wrap justify-content-between mb-2">
                            <div className="wrap">
                              <div className="icon-f">
                                <img src={getImageUrl(getRoundFlag(round))} alt="" />
                              </div>
                              <p className="mx-2 t-medium c-red">{displayTitle}</p>
                            </div>
                            <div className="bgc-9 bdr-5 px-2 c-white wrap justify-content-center">
                              <p>{displayCountdown}</p>
                            </div>
                          </div>
                          <p className="text-end c-dark fs-14 mb-2">ปิดรับ {closeTimeLabel}</p>

                          <div className="row mx--1 justify-content-center">
                            <div className="col-6 px--2 mb-2">
                              <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                                <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                                  <small>{packageLabel}</small>
                                </div>
                                <div className="px-1">
                                  <h5>฿ {fixedPrice}</h5>
                                </div>
                              </div>
                            </div>

                            <div className="col-6 px--2 mb-2">
                              <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                                <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                                  <small>ส่วนลด</small>
                                </div>
                                <div className="px-1">
                                  <h5>{fixedDiscount}%</h5>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 px--2 mb-3">
                              <h4 className="text-center t-medium">ระบุตัวเลข</h4>
                              <ul className="list-number">
                                {numbers.map((number, index) => (
                                  <li key={index}>
                                    <div className="inp-num bdr-5">
                                      <input 
                                        type="text" 
                                        value={number}
                                        onChange={(e) => handleNumberInput(index, e.target.value)}
                                        maxLength={1}
                                      />
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="col-8 px-1">
                              <div className="row mx--1">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                  <div key={num} className="col-4 px-1 mb-2">
                                    <button 
                                      className="button-num"
                                      onClick={() => handleNumberButton(num.toString())}
                                    >
                                      {num}
                                    </button>
                                  </div>
                                ))}
                                <div className="col-12 px-1 mb-2">
                                  <button 
                                    className="button-num"
                                    onClick={() => handleNumberButton('0')}
                                  >
                                    0
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="col-4 px-1">
                              <div className="row mx--1">
                                <div className="col-12 px-1 mb-2">
                                  <button
                                    className="button-click c-white bgg-1 bdr-5"
                                    onClick={handleBackspace}
                                  >
                                    <i className="fas fa-backspace"></i>
                                  </button>
                                </div>

                                <div className="col-12 px-1 mb-2">
                                  <button
                                    className="button-click c-white bgg-5 bdr-5"
                                    onClick={handleRandom}
                                  >
                                    สุ่มเลข
                                  </button>
                                </div>

                                <div className="col-12 px-1 mb-2">
                                  <button
                                    className="button-click c-white bgg-5 bdr-5"
                                    onClick={handleClear}
                                  >
                                    ล้างรายการ
                                  </button>
                                </div>

                                <div className="col-12 px-1 mb-2">
                                  <a
                                    href="#"
                                    className="button-click text-center wrap justify-content-center c-white bgg-4 bdr-5"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      navigate(backTo, { state: round ? { round } : undefined })
                                    }}
                                  >
                                    เปลี่ยนราคา
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 px-1">
                        <div className="">
                          <div className="row mx--1">
                            <div className="col-12 px-1 mb-2">
                              <div className="bdr-5 bgc-4 px-2 py-1 wrap justify-content-between">
                                <div className="wrap t-medium c-red">
                                  <i className="far fa-bookmark me-2"></i>
                                  ใส่ราคา
                                </div>
                              </div>
                            </div>

                            <div className="col-12 px-1 mb-2">
                              <div className="wrap justify-content-center bgc-4 c-white px-1 py-2 bdr-5">
                                <div className="row mx--1 align-items-center w-100">
                                  <div className="col-12 col-md-4 px--2 mb-2 mb-md-0">
                                    <div className="wrap">
                                      <input
                                        type="text"
                                        value={amount}
                                        onChange={(e) => handleAmountChange(e.target.value)}
                                        placeholder="ระบุจำนวนชุด"
                                        className="bdc-1 bdr-5 p-2"
                                        style={{ width: '65%' }}
                                      />
                                      <button
                                        className="py-2 bgg-1 border-0 bdr-5 c-white"
                                        style={{ width: '35%' }}
                                        onClick={() => setAmount((parseInt(amount) + 1).toString())}
                                      >
                                        <i className="fas fa-plus-square"></i>
                                      </button>
                                    </div>
                                  </div>

                                  <div className="col-12 col-md-8 px--2">
                                    <div className="">
                                      <div className="row mx--2">
                                        {[1, 2, 3, 4, 5, 10].map(sets => (
                                          <div key={sets} className="col-2 px--2">
                                            <button
                                              className={`bdr-5 bdc-4 px-0 py-2 w-100 ${
                                                selectedSets === sets 
                                                  ? 'bgg-1 c-white' 
                                                  : 'bgc-7 c-dark'
                                              }`}
                                              onClick={() => handleSetSelection(sets)}
                                            >
                                              <p className="">{sets} ชุด</p>
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-6 px-1 mb-2">
                              <div className="p-1 bgg-3 bdr-5 c-white w-100 text-center">
                                <small className="bgc-4 px-2 c-blue bdr-5">ยอดเครดิตคงเหลือ</small>
                                <h5 className="t-medium">
                                  {isLoadingWallet ? '...' : formatCurrency(walletBalance)}
                                </h5>
                              </div>
                            </div>

                            <div className="col-6 px-1 mb-2">
                              <div className="p-1 bgg-1 bdr-5 c-white w-100 text-center">
                                <small className="bgc-4 px-2 c-red bdr-5">รวมยอดแทง</small>
                                <h5 className="t-medium">{formatCurrency(totalBet)}</h5>
                              </div>
                            </div>

                            <div className="col-6 px-1 mb-2">
                              <button 
                                className="bgg-5 py-2 w-100 border-0 bdr-5"
                                onClick={handleClear}
                              >
                                <p className="c-white">
                                  <i className="fas fa-trash-alt me-2"></i>
                                  ล้างรายการ
                                </p>
                              </button>
                            </div>

                            <div className="col-6 px-1 mb-2">
                              <button 
                                className="bgg-4 py-2 w-100 border-0 bdr-5"
                                disabled={submitting || totalBet <= 0 || numbers.join('').length < 4}
                                onClick={handleSubmitBet}
                              >
                                <p className="c-white mb-0">
                                  {submitting ? (
                                    <><i className="fas fa-spinner fa-spin me-2"></i>กำลังส่ง...</>
                                  ) : (
                                    <><i className="fas fa-hand-pointer me-2"></i>ส่งโพย</>
                                  )}
                                </p>
                              </button>
                            </div>

                            <div className="col-12 px-1">
                              <div className="wrap justify-content-center bgc-4 c-white p-2 bdr-5 fs-14">
                                <div className="table-responsive w-100">
                                  <table className="table table-striped mb-0 table-bordered text-center">
                                    <thead className="table-dark">
                                      <tr>
                                        <th>ชนิด</th>
                                        <th>ชุดใหญ่</th>
                                        <th>ชุดกลาง</th>
                                        <th>ชุดเล็ก</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {setPayoutRows.map((row) => (
                                        <tr key={row.label}>
                                          <td>{row.label}</td>
                                          <td>{row.large}</td>
                                          <td>{row.medium}</td>
                                          <td>{row.small}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RulesGuideModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        scope={round ? {
          huayId: round.huayId,
          huayName: round.huayName,
          huayTypeId: round.huayTypeId,
          huayTypeName: round.huayTypeName,
        } : undefined}
        closeButtonLabel="ยอมรับและตกลง"
        fallbackRules={(
          <div className="text-center">
            <p>- ระบบเปิด-ปิดแทงตามเวลาที่ได้แจ้งไว้ และบริษัทสามารถพิจารณาปิดแทงก่อนกำหนดได้</p>
            <p>- ทางบริษัทสามารถคืนเลขได้ทุกกรณี แต่จะคืนก่อนที่จะออกผลรางวัล</p>
            <p>- การคืนเลขหรือคืนโพยของสมาชิก ต้องเป็นไปเงื่อนไขและเวลาที่ทางเว็บกำหนดเท่านั้น (<span style={{ color: '#ff0000' }}><strong>คืนโพยได้ภายใน {formatCustomerSlipCancelWindowText(cancelWindowMinutes)}หลังจากที่แทง</strong></span>)</p>
            <p>- หากหวยนั้นๆ มีการออกผลรางวัลก่อนที่จะปิดแทง บริษัทจะทำการยกเลิกรายการที่แทงเฉพาะบางเลขที่ได้ทำการแทงหลังจากหวยนั้นๆ ออกผล</p>
            <p>- ราคาจ่ายและส่วนลดจะลดลงตามเลขดังหรือเลขอั้น ให้สมาชิกตรวจสอบโพยและราคาจ่าย/ส่วนลดหลังการแทงทุกครั้ง</p>
            <p>- การสมัครสมาชิกหรือแนะนำชื่อตัวเองเพื่อปั่นค่าคอมมิชชั้น จะถูกพิจารณาปิดบัญชีและยึดเครดิตของบัญชีที่เกี่ยข้องทั้งหมด</p>
            <p>- สมาชิกที่จงใจเจตนาทุจริตในการเล่น จะถูกปิดบัญชีและไม่คืนเครดิตให้ทุกกรณี</p>
            <p>- กรณีที่เซฟเวอร์หรืออินเตอร์เน็ตล่มในช่วงการเปิดรับแทง และเกิดข้อโต้แย้งเกี่ยวกับการออกผลรางวัล ทางบริษัทมีสิทธิ์จะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ผลรางวัลคลุมเครือหรือมีแนวโน้มที่ไม่โปร่งใส ทางบริษัทจะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ในกรณีเกิดข้อพิพาทและโต้แย้งในการออกผลรางวัล การตัดสินของบริษัทถือเป็นข้อยุติและสิ้นสุด</p>
            <p>- <span style={{ color: '#ff0000' }}>สมาชิกที่สมัครมากกว่า 1 ยูสเซอร์ โดยใช้ชื่อเดียวกัน เพื่อผลประโยชน์ในการแทงได้มากกว่าหรือปั่นค่าแนะนำ จะถูกระงับและไม่คืนเครดิตให้ทุกรณี</span></p>
          </div>
        )}
        fallbackGuides={(
          <div className="mt-5">
            <p className="text-center"><strong><u>วิธีการดูผลรางวัล "{displayTitle}"</u></strong></p>
            <p className="text-center">ใช้รางวัลเลข 4 หลักของหวยชุด เช่น 6089</p>
            <p className="mb-4 text-center"><strong><u>การตรวจผลรางวัล</u></strong></p>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <tbody>
                  <tr><td style={{ width: '100px' }}>4 ตัวบน</td><td>6089</td></tr>
                  <tr><td>3 ตัวตรง</td><td>089</td></tr>
                  <tr><td>4 ตัวโต๊ด</td><td>6089 สลับตำแน่งทั้ง 4 หลัก</td></tr>
                  <tr><td>3 ตัวโต๊ด</td><td>089 098 809 890 908 980</td></tr>
                  <tr><td>2 ตัวหน้า</td><td>60</td></tr>
                  <tr><td>2 ตัวหลัง</td><td>89</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-center">**จำกัดสิทธิ์ 1 ชุดรับรางวัลใหญ่ที่สุด รางวัลเดียว**</p>
          </div>
        )}
      />
    </div>
  )
}

export default SetLottoGamePage
